import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  previewBookability,
  type BookabilityPreview,
  type InterviewLinkDraft,
} from '../../services/interviewLinksApi';
import { dayFmt, timeFmt } from './format';

/**
 * Turns the notice and earliest-day settings into a date.
 *
 * "48 hours" does not tell anyone that a candidate opening the link on Friday
 * afternoon cannot book before Monday — weekends and availability windows decide
 * that, and only the slot engine knows them. So the answer comes from the proxy
 * rather than being recomputed here, where it would drift.
 *
 * Debounced because it fires on every keystroke that touches the draft, and the
 * result is deliberately silent on failure: an incomplete draft answers 400, and
 * the form's own validation already says what is missing.
 */
export function BookabilityHint({ draft }: { draft: InterviewLinkDraft }) {
  const [preview, setPreview] = useState<BookabilityPreview | null>(null);
  const [checking, setChecking] = useState(false);

  /**
   * Serialised so the effect re-runs on a real change rather than on every new
   * object identity — the parent rebuilds the draft on each keystroke.
   *
   * Includes the fields that only affect VALIDATION (name, posting, stage) even
   * though they cannot move a slot. The proxy rejects an incomplete draft, and if
   * they were left out, filling them in afterwards would not change this key — so
   * a form completed in that order would sit on "no preview" forever.
   */
  const key = JSON.stringify({
    name: draft.name,
    postingId: draft.postingId,
    stageId: draft.stageId,
    minutes: draft.minutes,
    interval: draft.interval,
    minNoticeHours: draft.minNoticeHours,
    earliestBookableDays: draft.earliestBookableDays,
    offeredRangeDays: draft.offeredRangeDays,
    bufferBefore: draft.bufferBefore,
    bufferAfter: draft.bufferAfter,
    meetingType: draft.meetingType,
    interviewers: draft.interviewers,
  });

  // Written in a layout effect, not during render: assigning a ref while
  // rendering is a lint error and, worse, unsound under concurrent rendering.
  const draftRef = useRef(draft);
  useLayoutEffect(() => {
    draftRef.current = draft;
  });

  useEffect(() => {
    // No state change here — the render path below already handles the empty
    // case, and setting state synchronously in an effect cascades a re-render.
    if (draft.interviewers.length === 0) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setChecking(true);
      void previewBookability(draftRef.current).then((res) => {
        if (cancelled) return;
        setPreview(res);
        setChecking(false);
      });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the change
    // signal; depending on `draft` itself would refire on every render.
  }, [key, draft.interviewers.length]);

  if (draft.interviewers.length === 0) {
    return (
      <p className="mt-2 text-xs text-gray-500">
        Add an interviewer to see when a candidate could first book.
      </p>
    );
  }

  if (checking && !preview) {
    return <p className="mt-2 text-xs text-gray-400">Working out the first bookable time…</p>;
  }

  if (!preview) return null;

  if (!preview.bookableFrom) {
    return (
      <p className="mt-2 text-xs text-[#854F0B]">
        No bookable time at all in the next {draft.offeredRangeDays} days. The notice period and
        earliest day together rule out every slot in the offered window.
      </p>
    );
  }

  const when = new Date(preview.bookableFrom);
  return (
    <p className="mt-2 text-xs text-gray-600">
      A candidate opening this link now could book from{' '}
      <strong className="font-semibold text-gray-900">
        {dayFmt.format(when)}, {timeFmt.format(when)}
      </strong>{' '}
      — {preview.slotsInWindow} slot{preview.slotsInWindow === 1 ? '' : 's'} in the next{' '}
      {draft.offeredRangeDays} days, if the interviewers are free.
    </p>
  );
}
